// ----------------------------------------------------------------------------
// Snippet: Video Player
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import Vimeo from '@vimeo/player';

// Styles
import './_video-block__controls.scss';
import { forEach } from 'lodash-es';


// -------------------------------------
// Variables
// -------------------------------------

// Selectors
const VIDEO_BLOCK    = '.video-block';
const VIDEO          = '.video-block__video';
const YOUTUBE_VIDEO  = '.video-block--youtube';
const VIMEO_VIDEO    = '.video-block--vimeo';
const HTML5_VIDEO    = '.video-block--html5';
const CONTROLS_PLAY  = '.video-block__control--play';
const CONTROLS_PAUSE = '.video-block__control--pause';

// Classes
const POSTERED_CLASS      = 'video-block--is-postered';
const PAUSED_CLASS        = 'video-block--is-paused';
const DYNAMIC_VIDEO_CLASS = 'video-block--dynamic-video';

// Events
const playerPlay  = new Event('onplay.video-block');
const playerPause = new Event('onpause.video-block');
const play        = new Event('play.video-block');
const pause       = new Event('pause.video-block');


// -------------------------------------
// Helpers
// -------------------------------------

const videoBlocks = {
    init: function() {
        const videoBlockElems = document.querySelectorAll(VIDEO_BLOCK);

        videoBlockElems.forEach(function(videoBlock) {
            videoBlocks.initVideoBlock(videoBlock);
        });
    },
    initVideoBlock: function(videoBlock) {
        videoBlock.playButton = videoBlock.querySelector(CONTROLS_PLAY);
        videoBlock.pauseButton = videoBlock.querySelector(CONTROLS_PAUSE);
        videoBlock.querySelector(VIDEO).setAttribute('inert', '');

        videoBlock.addEventListener('onplay.video-block', function() {
            videoBlockControls.play(videoBlock);
        });

        videoBlock.addEventListener('onpause.video-block', function() {
            videoBlockControls.pause(videoBlock);
        });

        if (videoBlock.playButton !== null) {
            videoBlock.playButton.addEventListener('click', function() {
                videoBlockControls.play(videoBlock);
                videoBlock.dispatchEvent(play);
                videoBlock.pauseButton?.focus();
                videoBlock.querySelector(VIDEO).removeAttribute('inert');
            });
        }

        if (videoBlock.pauseButton !== null) {
            videoBlock.pauseButton.addEventListener('click', function() {
                videoBlockControls.pause(videoBlock);
                videoBlock.dispatchEvent(pause);
                videoBlock.playButton?.focus();
                videoBlock.querySelector(VIDEO).setAttribute('inert', '');
            });
        }
    }
}

const videoBlockControls = {
    play: function(videoBlock) {
        videoBlock.classList.remove(POSTERED_CLASS);
        videoBlock.classList.remove(PAUSED_CLASS);
    },
    pause: function(videoBlock) {
        videoBlock.classList.add(POSTERED_CLASS);
        videoBlock.classList.add(PAUSED_CLASS);
    }
}

const youtubeVideos = {
    init: function() {
        const tag            = document.createElement('script');
        const firstScriptTag = document.getElementsByTagName('script')[0];

        tag.src = 'https://www.youtube.com/iframe_api';
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = function() {
            youtubeVideos.initVideos();
        };
    },
    initVideos: function() {
        const videoBlocks = document.querySelectorAll(YOUTUBE_VIDEO);

        videoBlocks.forEach(function(videoBlock) {
            const video         = youtubeVideos.getVideo(videoBlock);
            const videoIframe   = document.createElement('div');
            const videoSettings = JSON.parse(video.dataset.videoSettings);
            const videoID       = videoSettings.video_id;
            video.appendChild(videoIframe);

            const player = new YT.Player(videoIframe, {
                videoId: videoID,
                playerVars: {
                    playlist      : videoSettings.playlist_id == null && videoSettings.is_looped ? videoID : null,
                    loop          : videoSettings.is_looped   ? 1 : 0,
                    autoplay      : videoSettings.is_autoplay ? 1 : 0,
                    controls      : videoSettings.is_controls ? 1 : 0,
                    disablekb     : videoSettings.is_controls ? 0 : 1,
                    playsinline   : videoSettings.is_autoplay ? 1 : 0,
                    showinfo      : 0,
                    modestbranding: 1,
                    fs            : 1,
                    cc_load_policy: 0,
                    iv_load_policy: 3,
                    rel           : 0
                },
                events: {
                    'onReady': youtubeVideos.onPlayerReady
                }
            });
        });
    },
    onPlayerReady: function(event) {
        const player        = event.target;
        const video         = youtubeVideos.getVideo(player.getIframe().closest(VIDEO_BLOCK));
        const videoBlock    = video.closest(VIDEO_BLOCK);
        const videoSettings = video.dataset.videoSettings;

        player.setVolume(videoSettings.is_muted ? 0 : 100);

        videoBlock.addEventListener('play.video-block', function() {
            youtubeVideos.play(player);
        });

        videoBlock.addEventListener('pause.video-block', function() {
            youtubeVideos.pause(player);
        });

        player.getIframe.tabIndex = -1;
    },
    play: function(player) {
        const iframe = player.getIframe();

        iframe.tabIndex = 0;
        player.playVideo();
    },
    pause: function(player) {
        player.pauseVideo();
    },
    getVideo: function (videoBlock) {
        return videoBlock.querySelector(VIDEO);
    }
}

const vimeoVideos = {
    init: function() {
        const videoBlocks = document.querySelectorAll(VIMEO_VIDEO);

        videoBlocks.forEach(function(videoBlock) {
            const video         = videoBlock.querySelector(VIDEO);
            const videoSettings = JSON.parse(video.dataset.videoSettings);
            const videoID       = videoSettings.video_id;

            if (videoSettings.lookup_poster) {
                const url = 'https://vimeo.com/api/v2/video/' + videoID + '.json';

                fetch(url)
                .then(response => response.json())
                .then(data => {
                    videoBlock.classList.add(POSTERED_CLASS);
                    videoBlock.style.backgroundImage = 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(' + data[0].thumbnail_large + ')';
                })
                .catch((error) => {
                    videoBlock.classList.remove(POSTERED_CLASS);
                    videoBlock.style.backgroundImage = '';
                });
            }

            const player = new Vimeo(video, {
                id:        videoID,
                loop:      videoSettings.is_looped,
                autoplay:  videoSettings.is_autoplay,
                autopause: !videoSettings.is_autoplay,
                title:     false,
                portrait:  false,
                byline:    false,
                controls:  videoSettings.is_controls
            });

            player.setVolume(videoSettings.is_muted ? 0 : 1);

            videoBlock.addEventListener('play.video-block', function() {
                vimeoVideos.play(player);
            });

            videoBlock.addEventListener('pause.video-block', function() {
                vimeoVideos.pause(player);
            });
        });
    },
    play: function(player) {
        player.play();
    },
    pause: function(player) {
        player.pause();
    }
}

const html5Videos = {
    init: function() {
        const videoBlocks = document.querySelectorAll(HTML5_VIDEO);

        videoBlocks.forEach(function(videoBlock) {
            const video = videoBlock.querySelector(VIDEO);

            // If this video block has dynamic videos on mobile and desktop, get the appropriate video sources on mobile/desktop
            if (videoBlock.classList.contains(DYNAMIC_VIDEO_CLASS)) {
                const videoSources            = JSON.parse(video.dataset.videoSources);
                const desktopVideoUrls        = videoSources.desktop_video_source_urls.split(',');
                const desktopVideoSourceTypes = videoSources.desktop_video_source_types.split(',');
                const mobileVideoUrls         = videoSources.mobile_video_source_urls.split(',');
                const mobileVideoSourceTypes  = videoSources.mobile_video_source_types.split(',');
                let sourceUrls;
                let sourceTypes;

                if (window.innerWidth > 680 || mobileVideoUrls[0] == "") { // Load desktop videos. Fallback to Desktop videos if there is no mobile video uploaded
                    sourceUrls  = desktopVideoUrls;
                    sourceTypes = desktopVideoSourceTypes;
                } else { // Load mobile videos
                    sourceUrls  = mobileVideoUrls;
                    sourceTypes = mobileVideoSourceTypes;
                }

                // Loop through all the source urls and types then insert the sources to the video element
                for (let index = 0; index < sourceUrls.length; index++) {
                    video.insertAdjacentHTML("beforeend", `<source type='${sourceTypes[index]}' src='${sourceUrls[index]}'/>`);
                }
            }

            videoBlock.addEventListener('play.video-block', function() {
                video.play();
            });

            videoBlock.addEventListener('pause.video-block', function() {
                video.pause();
            });

            video.onplay = function() {
                videoBlock.dispatchEvent(playerPlay);
            }

            video.onpause = function() {
                videoBlock.dispatchEvent(playerPause);
            }
        });
    }
}


// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function() {
    videoBlocks.init();
    youtubeVideos.init();
    vimeoVideos.init();
    html5Videos.init();
});

